/**
 * Lightweight A/B paywall + conversion tracking client.
 * - Reads the variant from /billing/entitlements (preferred — bound to user)
 *   or /paywall/assignment (anonymous visitors).
 * - Caches the result per session.
 * - Exposes track() to log events. Best-effort, never throws.
 *
 * The backend uses the variant to size the top-N preview server-side, so the
 * UI never needs to know that — it just renders whatever `businesses_hidden`
 * the API returns. This module is here so the COPY (banner, headline, CTA)
 * and the conversion EVENTS can be variant-aware.
 */
import api from './api';

const ANON_KEY = 'sdr_anon_id';
const VARIANT_KEY = 'sdr_paywall_variant';

const FALLBACK = {
  key: 'control',
  preview_businesses: 3,
  preview_districts: 3,
  banner_copy: "You're viewing a Pro feature preview",
  headline_copy: 'Pro feature: Economic Return + Ranked Businesses',
  cta_copy: 'Upgrade to Pro to unlock',
  allocated_pct: 100,
  active: true,
};

export function getAnonId() {
  let id = localStorage.getItem(ANON_KEY);
  if (!id) {
    id = (window.crypto?.randomUUID && window.crypto.randomUUID())
      || `anon_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem(ANON_KEY, id);
  }
  return id;
}

let inflight = null;
export async function getVariant(entitlementsVariant) {
  if (entitlementsVariant?.key) {
    sessionStorage.setItem(VARIANT_KEY, JSON.stringify(entitlementsVariant));
    return entitlementsVariant;
  }
  const cached = sessionStorage.getItem(VARIANT_KEY);
  if (cached) {
    try { return JSON.parse(cached); } catch {}
  }
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { data } = await api.get(`/paywall/assignment?anon_id=${encodeURIComponent(getAnonId())}`);
      if (data?.variant) {
        sessionStorage.setItem(VARIANT_KEY, JSON.stringify(data.variant));
        return data.variant;
      }
    } catch {}
    return FALLBACK;
  })();
  const v = await inflight;
  inflight = null;
  return v;
}

/**
 * Fire-and-forget event log.
 * @param {string} event_type  paywall_view | upgrade_cta_click | checkout_started | trial_started | subscribed
 * @param {object} props       { source, plan_id, feature, variant_key }
 */
export function track(event_type, props = {}) {
  let variant_key = props.variant_key;
  if (!variant_key) {
    try {
      const cached = sessionStorage.getItem(VARIANT_KEY);
      if (cached) variant_key = JSON.parse(cached).key;
    } catch {}
  }
  api.post('/paywall/event', {
    event_type,
    source: props.source,
    feature: props.feature,
    plan_id: props.plan_id,
    variant_key,
    anon_id: getAnonId(),
  }).catch(() => {});
}
